import React, { useState, useEffect, useRef, useMemo } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Tag, Tooltip, Tree, Input } from "antd"
import { faRefresh } from "@fortawesome/free-solid-svg-icons"
import { DownOutlined } from "@ant-design/icons"
import { Loader } from "shared/ui/Loader/Loader.component"
import { Editor } from "@tiptap/react"
import { CodebookData, AntTreeNode } from "types/codebook.types"
import {
  filterCodebookTree,
  getCodebookNodePath,
  transformCodebookToTreeData,
} from "utils/codebookUtils"
import {
  QC_BUILDER_DRAG_TYPE,
  QC_BUILDER_DRAG_LEAVES_TYPE,
} from "stores/queryBuilderStore"

interface DragInfo {
  event: React.DragEvent<HTMLDivElement>
  node: AntTreeNode
}

const { Search } = Input

// Define the props interface for the DataTree component
interface DataTreeProps {
  /**
   * The console's editor. Not read here: the console inserts clicked paths
   * into it through `onVariableClick`.
   */
  editor: Editor | null
  /** The codebook data to display in the tree */
  codebook: CodebookData
  /** Function to regenerate the codebook */
  regenerateCodebook: () => void
  /** Indicates if the codebook is currently being loaded or regenerated */
  isCodebookLoading: boolean
  /** Called with the `//`-joined path when a node is clicked. Absent = clicks do nothing. */
  onVariableClick?: (path: string) => void
}

// Define the selection info interface from Ant Design Tree
interface SelectionInfo {
  event: string
  selected: boolean
  node: AntTreeNode
  selectedNodes: AntTreeNode[]
  nativeEvent: MouseEvent
}

/**
 * Returns a compact Tag element for a given field type.
 */
const getTypeTag = (type: string): React.ReactNode => {
  const colorMap: Record<string, string> = {
    TABLE: "#36c3ed",
    BOOL: "#52c41a",
    INT: "#faad14",
    NUMBER: "#faad14",
    REAL: "#eb2f96",
    TEXT: "#722ed1",
    STRING: "#722ed1",
    TIMESTAMP: "#13c2c2",
    CAT: "#1890ff",
  }
  const color = colorMap[type] || "#666"
  const label = type === "NUMBER" ? "INT" : type === "STRING" ? "TEXT" : type
  return (
    <Tag color={color} style={{ fontSize: 9, lineHeight: '16px', padding: '0 3px', marginRight: 4, marginLeft: 0, marginTop: 0, marginBottom: 0, borderRadius: 3 }}>
      {label}
    </Tag>
  )
}

/**
 * AntD `titleRender` callback — wraps the raw string title with the
 * coloured type-tag pill. Defaults to "TABLE" for object-branch nodes
 * that don't carry an `originalData` field.
 */
const renderTreeTitle = (node: AntTreeNode): React.ReactNode => {
  const entryType = node.originalData?.type ?? "TABLE"
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        fontSize: 12,
      }}
    >
      {getTypeTag(entryType)}
      {node.title}
    </span>
  )
}

/**
 * DataTree component displays a hierarchical view of the codebook data.
 * It supports searching, dynamic tree height adjustments, dragging nodes into
 * the Builder, and reporting clicked nodes through `onVariableClick`.
 *
 * @component
 * @param props - The properties passed to the component.
 */
const DataTree: React.FC<DataTreeProps> = ({
  codebook,
  regenerateCodebook,
  isCodebookLoading,
  onVariableClick,
}) => {
  const [treeData, setTreeData] = useState<AntTreeNode[]>([])
  const [treeHeight, setTreeHeight] = useState<number>(0)
  const [searchTerm, setSearchTerm] = useState<string>("")
  const treeContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (codebook) {
      setTreeData(transformCodebookToTreeData(codebook))
    }
  }, [codebook])

  // The virtual Tree needs a fixed pixel height, so we track the container's
  // size. The container div does NOT exist while the codebook loader shows
  // (early return below) — the effect must re-run when loading finishes, and
  // must observe the element rather than measure once: measuring only at
  // mount left the tree permanently blank whenever the component first
  // mounted mid-load or before the pane's layout had settled to its real
  // height. The observer also covers split-drag and window resizes.
  useEffect(() => {
    const container = treeContainerRef.current
    if (!container) return

    const updateTreeHeight = (): void => {
      setTreeHeight(container.clientHeight)
    }

    updateTreeHeight()
    const observer = new ResizeObserver(updateTreeHeight)
    observer.observe(container)

    return () => observer.disconnect()
  }, [isCodebookLoading])

  /**
   * Retrieves the full path of a node given its key.
   *
   * @param key - The key of the node.
   * @returns An array representing the path from root to the node.
   */
  const getNodePath = (key: string): string[] =>
    getCodebookNodePath(treeData, key)

  /**
   * Handles the selection of a node in the tree.
   * Passes the full `//`-joined path of the clicked node to `onVariableClick`.
   *
   * @param _selectedKeys - The keys of the selected nodes (unused).
   * @param info - Information about the selection event.
   */
  const onSelect = (_selectedKeys: React.Key[], info: SelectionInfo): void => {
    if (!onVariableClick) return
    const path = getNodePath(String(info.node.key))
    onVariableClick(path.join("//"))
  }

  /**
   * Recursively collect leaf paths under a tree node. parentSegments is the
   * path UP TO (not including) the node. Used by drag start so that drop
   * sites which need leaf-level granularity (e.g. Conditions) can expand a
   * parent QTable into its individual answer options.
   */
  const collectLeafPaths = (
    node: AntTreeNode,
    parentSegments: string[]
  ): string[] => {
    const segments = [...parentSegments, node.title]
    if (node.isLeaf || !node.children || node.children.length === 0) {
      return [segments.join("//")]
    }
    return node.children.flatMap((child) =>
      collectLeafPaths(child, segments)
    )
  }

  /**
   * Native HTML5 drag start handler for tree nodes. Sets the variable's
   * Data(Q) path on the dataTransfer so the Builder's drop zones can
   * consume it, plus a JSON array of all leaf descendants for drop sites
   * that need to expand parent QTables into their answer options.
   */
  const onDragStart = (info: DragInfo): void => {
    const { event, node } = info
    const path = getNodePath(String(node.key))
    if (path.length === 0) return
    const variable = path.join("//")
    const leafPaths = collectLeafPaths(node, path.slice(0, -1))
    if (event.dataTransfer) {
      event.dataTransfer.setData(QC_BUILDER_DRAG_TYPE, variable)
      event.dataTransfer.setData(
        QC_BUILDER_DRAG_LEAVES_TYPE,
        JSON.stringify(leafPaths)
      )
      event.dataTransfer.setData("text/plain", variable)
      event.dataTransfer.effectAllowed = "copy"
    }
  }

  /**
   * Filters the tree data based on the search term.
   */
  const filteredTreeData = useMemo(
    (): AntTreeNode[] => filterCodebookTree(treeData, searchTerm),
    [treeData, searchTerm]
  )

  /**
   * Handles changes in the search input.
   *
   * @param value - The current value of the search input.
   */
  const handleSearch = (value: string): void => {
    setSearchTerm(value)
  }

  if (isCodebookLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <Loader fontSize={48} />
        <span>Regenerating Data Tree...</span>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      <div className="flex items-center w-full p-2 bg-white border-b border-gray-100 shadow-sm">
        <Search
          placeholder="Search variables..."
          onChange={(e) => handleSearch(e.target.value)}
          allowClear
          className="flex-1 mr-2"
        />
        <Tooltip title="Regenerate Data Tree">
          <FontAwesomeIcon
            icon={faRefresh}
            onClick={regenerateCodebook}
            className="cursor-pointer text-sm"
            style={{ color: "var(--dq-brand)" }}
          />
        </Tooltip>
      </div>
      <div ref={treeContainerRef} className="flex-grow overflow-hidden pt-1">
        {treeHeight > 0 && (
          <Tree
            virtual
            showLine
            height={treeHeight}
            treeData={filteredTreeData}
            onSelect={onSelect}
            switcherIcon={<DownOutlined />}
            draggable={{ icon: false }}
            onDragStart={onDragStart}
            titleRender={renderTreeTitle}
            className="dataq-tree text-xs"
          />
        )}
      </div>
      <style>{`
        .dataq-tree .ant-tree-node-content-wrapper {
          border-radius: 4px;
          transition: background 0.15s;
          margin-left: 0 !important;
          padding-left: 2px !important;
        }
        .dataq-tree .ant-tree-node-content-wrapper:hover {
          background: #e6f7ff !important;
        }
        .dataq-tree .ant-tree-treenode {
          padding: 1px 0;
        }
        .dataq-tree .ant-tree-switcher {
          background: transparent !important;
          box-shadow: none !important;
          border: none !important;
          outline: none !important;
          min-width: 20px;
          width: 20px;
        }
        .dataq-tree .ant-tree-switcher::before {
          display: none !important;
        }
        .dataq-tree .ant-tree-switcher:hover {
          background: transparent !important;
          box-shadow: none !important;
        }
        .dataq-tree .ant-tree-switcher:focus {
          box-shadow: none !important;
        }
      `}</style>
    </div>
  )
}

export default DataTree
